import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import React from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { createBrowserClient } from "@supabase/ssr";

export const Route = createFileRoute("/login")({
  component: Login,
  validateSearch: (search) => {
    return { next: search.next ? String(search.next) : null };
  },
});

const supabase = createBrowserClient(
  import.meta.env.SUPABASE_URL,
  import.meta.env.SUPABASE_ANON_KEY,
);

function Login() {
  const { next } = Route.useSearch();
  const router = useRouter();

  if (import.meta.env.DEV) {
    const testUserEmail = import.meta.env.TESTUSER_EMAIL;
    const testUserPassword = import.meta.env.TESTUSER_PASSWORD;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      if (testUserEmail && testUserPassword) {
        const autoLogin = async () => {
          const { error } = await supabase.auth.signInWithPassword({
            email: testUserEmail,
            password: testUserPassword,
          });
          if (!error) {
            router.history.push(next ?? "/");
          } else {
            console.error(error);
            toast.error(`Failed to login: ${error.message}`);
          }
        };
        toast.info("Auto logging in with test account...");
        autoLogin();
      }
    }, []);
  }

  return (
    <Card className="mx-auto max-w-sm">
      <CardHeader>
        <CardTitle className="text-2xl">Login</CardTitle>
        <CardDescription>Login with your Farmless account</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4">
          <Button
            onClick={async () => {
              const origin = window.location.origin;
              const path = encodeURIComponent(next ?? "/");
              await supabase.auth.signInWithOAuth({
                provider: "google",
                options: {
                  redirectTo: `${origin}/auth/callback?next=${path}`,
                },
              });
            }}
            variant="outline"
            className="w-full"
          >
            <svg
              viewBox="0 0 48 48"
              className={"h-5 w-auto mr-4"}
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>Google</title>
              <path
                d="m43.611 20.083h-1.611v-.083h-18v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657c-3.572-3.329-8.35-5.382-13.618-5.382-11.045 0-20 8.955-20 20s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
                fill="#ffc107"
              />
              <path
                d="m6.306 14.691 6.571 4.819c1.778-4.402 6.084-7.51 11.123-7.51 3.059 0 5.842 1.154 7.961 3.039l5.657-5.657c-3.572-3.329-8.35-5.382-13.618-5.382-7.682 0-14.344 4.337-17.694 10.691z"
                fill="#ff3d00"
              />
              <path
                d="m24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238c-2.008 1.521-4.504 2.43-7.219 2.43-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025c3.31 6.477 10.032 10.921 17.805 10.921z"
                fill="#4caf50"
              />
              <path
                d="m43.611 20.083h-1.611v-.083h-18v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238c-.438.398 6.591-4.807 6.591-14.807 0-1.341-.138-2.65-.389-3.917z"
                fill="#1976d2"
              />
            </svg>
            Login with Google
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
